/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import React, { useRef, useEffect } from 'react';
import { css, StyleSheet } from 'aphrodite';

import { Checkbox, Text, Mixins, BrandColors } from '@blend/enterprise-components';
import { getSafeId } from './utils';
import { TypeaheadMultiSelect } from './TypeaheadMultiSelect';
import { noop } from '@utils/lodash';

const styles = StyleSheet.create({
  checkboxWrapper: {
    paddingRight: 5,
  },
  suggestionWrapper: {
    ...Mixins.borderBottom('transparent', 1),
    ...Mixins.border('transparent', 1, 'solid', 'borderTop'),
    ...Mixins.paddingVertical(5),
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    paddingLeft: 5,
    ':hover': {
      backgroundColor: BrandColors.NEUTRAL_25,
    },
  },
  lastSuggestion: {
    ':hover': {
      ...Mixins.borderBottom('transparent', 1),
    },
  },
  suggestionKeyboardFocused: {
    backgroundColor: BrandColors.NEUTRAL_75,
  },
  exclusiveSuggestion: {
    backgroundColor: BrandColors.NEUTRAL_25,
    ...Mixins.borderBottom(BrandColors.NEUTRAL_150, 1),
  },
});

interface TypeaheadMultiSelectSuggestionProps {
  suggestion: TypeaheadMultiSelect.Suggestion; // The suggestion to display
  suggestionProps: { index: number; key: string; isLastSuggestion: boolean };
  onSelectSuggestion: (suggestion: TypeaheadMultiSelect.Suggestion) => void;
  shouldKeyboardFocus: boolean; // To display style on the suggestion item when keyboard focused
  typeaheadId: string;
  isChecked: boolean;
}

/**
 *
 * @returns a suggestion listitem for the dropdown list
 */
export const TypeaheadMultiSelectSuggestion = (props: TypeaheadMultiSelectSuggestionProps) => {
  const { suggestionProps, shouldKeyboardFocus, suggestion, onSelectSuggestion, isChecked, typeaheadId } = props;

  const { index, key, isLastSuggestion } = suggestionProps;

  const suggestionRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (shouldKeyboardFocus) {
      /* istanbul ignore next */
      suggestionRef.current?.scrollIntoView(false);
    }
  }, [shouldKeyboardFocus]);
  return (
    <li
      id={getSafeId(`${typeaheadId}-${key}-suggestion`)}
      data-it={`TypeaheadMultiSelect-Suggestion-${suggestion.display}`}
      data-track-element-index={index}
      className={css(
        styles.suggestionWrapper,
        isLastSuggestion && styles.lastSuggestion,
        suggestion.exclusive && styles.exclusiveSuggestion,
        shouldKeyboardFocus && styles.suggestionKeyboardFocused,
      )}
      onClick={event => {
        event.stopPropagation();
        onSelectSuggestion(suggestion);
      }}
      role="option"
      aria-selected={isChecked}
      ref={suggestionRef}
    >
      <div className={css(styles.checkboxWrapper)}>
        <Checkbox
          id={getSafeId(`${typeaheadId}-${key}-checkbox`)}
          data-it="StaticTypeaheadMultiselect-option"
          checked={isChecked}
          onChange={noop}
          onClick={e => {
            e.stopPropagation();
            onSelectSuggestion(suggestion);
          }}
          onLabelClick={/* istanbul ignore next */ e => e.stopPropagation()}
        />
      </div>
      <Text fontSize="md" colorName="NEUTRAL_700">
        {suggestion.display}
      </Text>
    </li>
  );
};
